/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, Button, Collapse, Tooltip, FlexLayout, FlexItem, SVGIcon, keyboardUtils } from '@waca/ca-ui-toolkit';
import menuOverflowVertical16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--vertical_16.js';
import draggable16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/draggable_16.js';
import chevronDown16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-down_16';
import chevronUp16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-up_16';
import stringResource from 'ba-common-nls';
import styles from './ExpandableItemContainer.scss';

/**
 * The common ExpandableItemContainer component
 */

const { getKeyName, keyNames } = keyboardUtils;
const itemContainerClassName = styles.itemContainerClassName.replace(/"/g, '');
export default class ExpandableItemContainer extends Component<any, any> {

	state = {
		expanded: this.props.initiallyExpanded,
	}

	static propTypes = {
		className: PropTypes.string,
		label: PropTypes.node.isRequired,
		extendedInfo: PropTypes.node,
		itemGraphics: PropTypes.node,
		draggable: PropTypes.bool,
		initiallyExpanded: PropTypes.bool,
		required: PropTypes.bool,
		showActionMenu: PropTypes.bool,
		onExpandAction: PropTypes.func,
		onContextMenuAction: PropTypes.func,
	};

	static defaultProps = {
		className: '',
		extendedInfo: null,
		itemGraphics: null,
		draggable: true,
		expandable: true,
		initiallyExpanded: false,
		required: false,
		showActionMenu: true,
		onExpandAction: () => { },
		onContextMenuAction: () => { },
	};

	_onKeyDown = (e, onKeyDown) => {
		const { ENTER } = keyNames;
		switch (getKeyName(e)) {
			case ENTER: {
				onKeyDown && onKeyDown(e);
				break;
			}
			default:
				break;
		}
	};

	_onContextMenu = e => {
		e.stopPropagation();
		this.props.onContextMenuAction(e.pageX, e.pageY);
	};

	_toggleExpanded = e => {
		e.stopPropagation();
		this.props.onExpandAction && this.props.onExpandAction(!this.state.expanded);
		this.setState((state) => ({ expanded: !state.expanded }))
	};

	_renderExtendedInfo() {
		const { extendedInfo } = this.props;

		if (extendedInfo) {
			return (
				<FlexLayout className={`${itemContainerClassName}__infoLine`}>
					<FlexItem className={`${itemContainerClassName}__infoLine__values`}>
						{extendedInfo}
					</FlexItem>
				</FlexLayout>
			);
		}
	}

	_renderExpandableContent() {
		const { children } = this.props;

		return this.state.expanded && (
			<FlexLayout direction="column" className={`${itemContainerClassName}__body`}>
				<Collapse
					hasNestedCollapse
					forceInitialAnimation
					autoUnmount
					isOpened={this.state.expanded}
					className={`${itemContainerClassName}__body__collapse`}
				>
					<div
						onClick={e => {
							e.stopPropagation();
						}}
						onKeyDown={e => {
							e.stopPropagation();
						}}
					>
						{children}
					</div>
				</Collapse>
			</FlexLayout>
		)
	}

	render() {
		const {
			label,
			className,
			draggable,
			expandable,
			itemGraphics,
			onContextMenuAction,
			showActionMenu,
			required,
		} = this.props;

		const { expanded } = this.state;

		const classNames = cx(itemContainerClassName, {
			'is-draggable': draggable,
			'is-required': required
		});

		return (
			<FlexLayout
				direction="column"
				className={className ? `${classNames} ${className}` : classNames}
				tabIndex="0"
			>
				<FlexItem>
					<FlexLayout className={`${itemContainerClassName}__nameLine`}>
						{draggable && (
							<FlexItem className={`${itemContainerClassName}__nameLine__draggable`}>
								<SVGIcon size="small" iconId={draggable16.id} />
							</FlexItem>
						)}
						<FlexItem grow shrink basis="auto" className={`${itemContainerClassName}__nameLine__itemName`}>
							{label}
							{required && <div className={`${itemContainerClassName}__nameLine__itemName__requiredSymbol`}>*</div>}
						</FlexItem>
						{itemGraphics && (
							<FlexItem className={`${itemContainerClassName}__nameLine__icon`}>{itemGraphics}</FlexItem>
						)}
						{expandable && (
							<FlexItem
								className={`${itemContainerClassName}__nameLine__expandArrow`}
								tabIndex={0}
								onKeyPress={(e) => this._onKeyDown(e, this._toggleExpanded)}
								onClick={this._toggleExpanded}
							>
								<Tooltip title={expanded ? stringResource.get('collapse') : stringResource.get('expand')}>
									<SVGIcon size="small" iconId={expanded ? chevronUp16.id : chevronDown16.id} />
								</Tooltip>
							</FlexItem>
						)}
						{showActionMenu && onContextMenuAction && (
							<FlexItem>
								<Button
									className={`${itemContainerClassName}__nameLine__contextMenu`}
									data-tid={`${itemContainerClassName}__nameLine__contextMenu`}
									button={false}
									intent=""
									size="small"
									onClick={this._onContextMenu}
									onKeyPress={(e) => this._onKeyDown(e, this._onContextMenu)}
									variant="inline"
									tabIndex={0}
									icon={
										<SVGIcon pad iconId={menuOverflowVertical16.id} size="small" />
									}
								/>
							</FlexItem>
						)}
					</FlexLayout>
				</FlexItem>
				<FlexItem>
					{this._renderExtendedInfo()}
				</FlexItem>
				<FlexItem gutter={[0, 2]}>
					{this._renderExpandableContent()}
				</FlexItem>
			</FlexLayout>
		);
	}
}
